/**
 * Single source of truth for blog post metadata.
 *
 * Imported by:
 *   - client (src/pages/BlogArticle.tsx)     — drives React Helmet meta, the article header, etc.
 *   - server (server/index.ts)                — drives /blog/:slug HTML rewriting so crawlers
 *                                               (LinkedIn, X, Facebook, Slack, Google) see
 *                                               correct og:* / twitter:* / JSON-LD without
 *                                               executing JavaScript.
 *
 * Why both sides need this: React Helmet only updates `<head>` after JS runs. Social-card
 * crawlers do not execute JS, so they only see what the server emits in the initial HTML.
 * If only one of {client, server} has the right metadata, the preview is wrong for the other.
 */

export interface BlogArticleData {
  id: string;
  title: string;
  author: string;
  authorTitle: string;
  /** Human-readable display date, e.g. "May 16, 2026". */
  date: string;
  /** ISO-8601 date (YYYY-MM-DD). Drives article:published_time + JSON-LD datePublished. */
  dateISO: string;
  readTime: string;
  tags: string[];
  excerpt: string;
  /**
   * Path under /public used as og:image + twitter:image. Must resolve to a stable
   * absolute https URL — LinkedIn rejects relative URLs and will show no preview.
   * Files MUST live under apps/aiscendus-website/public/ so they are served by
   * express.static (not bundled with hashed names by Vite, which crawlers can't see).
   */
  socialImage: string;
  /**
   * Actual pixel dimensions of socialImage. Emitted as og:image:width/height so
   * crawlers can lay out the card before downloading the file. These MUST match
   * the real file — a wrong value makes LinkedIn/Facebook mis-render the preview.
   */
  socialImageWidth: number;
  socialImageHeight: number;
}

export const blogArticles: Record<string, BlogArticleData> = {
  "navigating-ethical-ai-landscape": {
    id: "navigating-ethical-ai-landscape",
    title: "Navigating the Ethical AI Landscape: HIPAA, SOC 2, and Agentic Healthcare",
    author: "Murugu Swaminathan",
    authorTitle: "President, Aiscendus Inc",
    date: "October 20, 2025",
    dateISO: "2025-10-20",
    readTime: "12 min read",
    tags: ["HIPAA Compliance", "SOC 2", "AI Ethics"],
    excerpt: "A comprehensive guide to regulatory compliance in healthcare AI, covering HIPAA requirements, SOC 2 Type II certification, and the unique challenges of implementing agentic AI systems in clinical environments.",
    socialImage: "/assets/website/blogs/hipaa-soc2.jpg",
    socialImageWidth: 829,
    socialImageHeight: 872,
  },
  "improving-patient-centric-value-based-care": {
    id: "improving-patient-centric-value-based-care",
    title: "Improving Patient-Centric, Value-Based Care through Emerging AI Technologies",
    author: "E. Llewellyn Weeks",
    authorTitle: "Chief Information Officer, Aiscendus",
    date: "November 15, 2025",
    dateISO: "2025-11-15",
    readTime: "8 min read",
    tags: ["Agentic AI", "Value-Based Care", "Patient-Centric"],
    excerpt: "Exploring how Agentic AI transforms healthcare delivery through intelligent, adaptive systems that balance technology and humanity, empowering both clinicians and patients for better outcomes.",
    socialImage: "/assets/website/blogs/hipaa-soc2.jpg",
    socialImageWidth: 829,
    socialImageHeight: 872,
  },
  "building-customer-trust-ai-platforms": {
    id: "building-customer-trust-ai-platforms",
    title: "Building Customer Trust in AI Platforms: How Secure, Intelligent Automation Powers Confident Healthcare Decisions",
    author: "Nicole Cella",
    authorTitle: "Chief Information Security Officer, Aiscendus",
    date: "February 13, 2026",
    dateISO: "2026-02-13",
    readTime: "6 min read",
    tags: ["Security", "Trust", "Healthcare AI", "Governance"],
    excerpt: "From a CISO's perspective, customer trust in AI platforms is built through deliberate design choices and consistent execution—aligning security, privacy, and compliance with how AI-powered automation functions in real-world healthcare environments.",
    socialImage: "/assets/website/blogs/hipaa-soc2.jpg",
    socialImageWidth: 829,
    socialImageHeight: 872,
  },
  "why-rust-for-healthcare-agentic-ai": {
    id: "why-rust-for-healthcare-agentic-ai",
    title: "Healthcare Agentic AI — Why We Chose Rust Over Python",
    author: "Senthil Ramachandran",
    authorTitle: "Chief Technology Officer, AIscendus",
    date: "March 21, 2026",
    dateISO: "2026-03-21",
    readTime: "8 min read",
    tags: ["Rust", "Agentic AI", "Healthcare Infrastructure", "Engineering"],
    excerpt: "When we evaluated our technology choices through the lens of what healthcare actually demands — memory safety, type safety, real concurrency, and deterministic behavior — the answer wasn't Python. It was Rust. And if no existing agentic framework was built for healthcare's constraints, we'd have to build our own.",
    socialImage: "/assets/website/blogs/Why-Rust-Healthcare-AI.png",
    socialImageWidth: 1241,
    socialImageHeight: 407,
  },
  "from-chart-to-claim-urgent-care": {
    id: "from-chart-to-claim-urgent-care",
    title: "From Reactive to Proactive: How AIscendus Stops Denials Before They Start",
    author: "Senthil Ramachandran",
    authorTitle: "Chief Technology Officer, AIscendus",
    date: "March 20, 2026",
    dateISO: "2026-03-20",
    readTime: "11 min read",
    tags: ["Revenue Cycle Management", "Agentic AI", "Urgent Care", "RCM"],
    excerpt: "Urgent care bleeds revenue through preventable denials — 12% denial rates with 84% potentially avoidable. AIscendus breaks the reactive cycle by working alongside your team as an expert partner at every stage, engineering claims for payment from the moment a patient walks in.",
    socialImage: "/assets/website/blogs/RCM-Chart-to-Claim-Pipeline.png",
    socialImageWidth: 1991,
    socialImageHeight: 761,
  },
  "uca-continuous-compliance": {
    id: "uca-continuous-compliance",
    title: "UCA: The Rise of AI-Driven Continuous Compliance",
    author: "Nicole Cella",
    authorTitle: "Chief Information Security Officer, Aiscendus",
    date: "March 21, 2026",
    dateISO: "2026-03-21",
    readTime: "10 min read",
    tags: ["Compliance", "HIPAA", "Urgent Care", "Continuous Assurance", "AI Governance"],
    excerpt: "Urgent care compliance has outgrown the binder. With evolving HIPAA rules, the reality of evidence decay, and UCA accreditation demands, the question is no longer whether you passed your last audit, it's whether you could pass one right now.",
    socialImage: "/assets/website/blogs/hipaa-soc2.jpg",
    socialImageWidth: 829,
    socialImageHeight: 872,
  },
  "agentic-ai-fsm-not-vibes": {
    id: "agentic-ai-fsm-not-vibes",
    title: "Why Agentic AI Needs a Finite State Machine, Not Vibes",
    author: "Senthil Ramachandran",
    authorTitle: "Chief Technology Officer, AIscendus",
    date: "May 16, 2026",
    dateISO: "2026-05-16",
    readTime: "13 min read",
    tags: ["Agentic AI", "Context Engineering", "Architecture", "Healthcare AI", "Compliance"],
    excerpt: "The gap between agentic AI demos and shippable agentic AI isn't about model capability — it's about architecture. We learned the hard way building ContextEngine for healthcare: the model must run inside deterministic, reviewable rails, or it can't be shipped in any regulated domain.",
    socialImage: "/assets/website/blogs/context_engine_fsm.png",
    socialImageWidth: 1536,
    socialImageHeight: 1024,
  },
};
